import { ColumnConfig } from '../../tables/column-picker/column-picker.component';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { AlertController, ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import { ApiService } from '../../../api/api.service';
import { GlobalService } from '../../../api/global.service';
import { SignaturePage } from '../../../secure-email/signature/signature.page';
import { CreatePage } from '../../../secure-email/create/create.page';
import { DetailsPage } from '../../../secure-email/details/details.page';
import { TemplatesPage } from '../../../secure-email/templates/templates.page';
import { DeletePage } from '../../../secure-email/delete/delete.page';
import { DraftsPage } from '../../../secure-email/drafts/drafts.page';
import { ManageComponent as TagsManageComponent } from '../../tags/manage/manage.component';
import { QuickCommentComponent } from '../../comments/quick-add/quick-add.component';
import { EmailTypes } from '../../../secure-email/EmailTypes';
import { DetailsComponent as TeamMemberDetailsPage } from '../../team-members/details/details.component';
import { DetailsPage as ClientDetailsPage } from '../../../clients/details/details.page';
import { DetailsPage as CompanyDetailsPage } from '../../../companies/details/details.page';

@Component({
  selector: 'app-email-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class EmailTableComponent implements OnInit, OnChanges {

  @Input() url_mod: string = '';
  @Input() hide_controls: boolean = false;
  // Column keys to hide (see availableColumns for the full list). Used by the home page, which lets the
  // user pick columns without touching this table anywhere else.
  @Input() hideColumns: string[] = [];
  availableColumns: ColumnConfig[] = [
    { key: 'sent_by', label: 'Sent By', visible: true },
    { key: 'sent_to', label: 'Sent To', visible: true },
    { key: 'status', label: 'System Status', visible: true },
    { key: 'type', label: 'Type', visible: true },
    { key: 'subject', label: 'Subject', visible: true },
    { key: 'sent_date', label: 'Sent Date', visible: true },
    { key: 'internal_status', label: 'Internal Status', visible: true },
    { key: 'auto_remind', label: 'Auto Remind', visible: true },
    { key: 'details', label: 'Details', visible: true },
    { key: 'comment', label: 'Comment', visible: true },
    { key: 'delete', label: 'Delete', visible: true },
    { key: 'resend', label: 'Resend', visible: true },
  ];

  all_emails: any[] = [];
  emails: any[] = [];
  loaded_emails = false;
  skeleton = new Array(10);
  emailInternalStatuses: any[] = [];

  current_start_date: Date;
  current_end_date: Date;
  pendingStartDate: string = '';
  pendingEndDate: string = '';
  readonly maxDate = dayjs().format('YYYY-MM-DD');

  emails_view = 'all';
  emails_source_filter = 'all';
  send_method_filter = 'all';
  team_members: any[] = [];
  selected_team_member = '';

  @ViewChild('email_dt') emailTable: Table;
  activeEmailFilterLabels: string[] = [];
  private readonly emailFilterLabelMap: { [field: string]: string } = {
    creator_display_name: 'Sent By',
    linked_display_name: 'Linked To',
    status: 'Status',
    type: 'Type',
    subject: 'Subject',
    created_date: 'Date',
  };

  singleMatchModeOptions = [
    { label: 'Includes Any', value: 'singleIncludesAny' },
    { label: 'Includes All', value: 'singleIncludesAll' },
  ];

  emailStatusOptions = [
    { label: 'Unopened', value: 0 },
    { label: 'Viewed', value: 1 },
    { label: 'Files Downloaded', value: 2 },
    { label: 'Completed', value: 3 },
  ];

  emailTypeOptions = [
    { label: 'Email', value: EmailTypes.email },
    { label: 'Document Request', value: EmailTypes.document_request },
    { label: 'Signature Request', value: EmailTypes.signature_request },
    { label: 'Approval Request', value: EmailTypes.approval_request },
    { label: 'Form Request', value: EmailTypes.survey },
    { label: 'Payment Request', value: EmailTypes.payment_request },
    { label: 'Appointment Request', value: EmailTypes.appointment_request },
    { label: 'Question Request', value: EmailTypes.question_request },
    { label: 'Checklist Request', value: EmailTypes.checklist_request },
  ];

  onEmailTableFilter(event: any) {
    this.activeEmailFilterLabels = this.computeActiveFilterLabels(event.filters, this.emailFilterLabelMap);
  }

  clearEmailFilters() {
    if (this.emailTable) this.clearTableFilters(this.emailTable);
    this.activeEmailFilterLabels = [];
  }

  private clearTableFilters(t: Table): void {
    if (!t) return;
    (t as any).tableService.onResetChange();
    if ((t as any).isStateful()) (t as any).clearState();
    (t as any)._filter();
  }

  private computeActiveFilterLabels(filters: any, labelMap: { [field: string]: string }): string[] {
    if (!filters) return [];
    const active: string[] = [];
    for (const field of Object.keys(labelMap)) {
      const raw = filters[field];
      if (!raw) continue;
      const constraints = Array.isArray(raw) ? raw : [raw];
      const hasValue = constraints.some((c: any) => {
        if (c.value === null || c.value === undefined) return false;
        if (Array.isArray(c.value)) return c.value.length > 0;
        if (typeof c.value === 'string') return c.value.trim().length > 0;
        return true;
      });
      if (hasValue) active.push(labelMap[field]);
    }
    return active;
  }

  constructor(
    private apiService: ApiService,
    private modalController: ModalController,
    public globalService: GlobalService,
    private alertController: AlertController
  ) { }

  ngOnInit() {
    this.FormatDates();
    this.GetEmailInternalStatuses();
    this.GetTeamMembers();
    this.GetData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.url_mod && !changes.url_mod.firstChange) {
      this.GetData();
    }
  }

  async GetTeamMembers() {
    const data: any = await this.apiService.get('team_members');
    if (data.success) this.team_members = data.data;
  }

  async GetData() {
    this.loaded_emails = false;
    this.emails = [];
    this.all_emails = [];
    let url: string;
    if (this.url_mod) {
      url = `emails?${this.url_mod}`;
    } else {
      const start_date = this.current_start_date.toISOString();
      const end_date = this.current_end_date.toISOString();
      url = `emails?start_date=${start_date}&end_date=${end_date}`;
    }
    const data: any = await this.apiService.get(url);
    if (data.success && data.data) this.all_emails = data.data;
    else if (!data.success) this.globalService.presentToastMessage(data.message, data.success);
    this.ApplyViewFilter();
    this.loaded_emails = true;
  }

  ApplyViewFilter() {
    let filtered = [...this.all_emails];

    // Source filter
    if (this.emails_source_filter === 'manual') {
      filtered = filtered.filter(e => e.show_in_list == 1 && !e.missing_info_guid);
    } else if (this.emails_source_filter === 'project') {
      filtered = filtered.filter(e => e.show_in_list == 0);
    } else if (this.emails_source_filter === 'missing_info') {
      filtered = filtered.filter(e => !!e.missing_info_guid);
    }

    // Send method filter - emails sent before send_method tracking existed have no value
    // recorded, so treat those as Fusion Workflow (the default/original send path). "personal"
    // covers both Outlook and Gmail - a team member can only have one linked at a time, but this
    // filter shouldn't care which.
    if (this.send_method_filter === 'fusion') {
      filtered = filtered.filter(e => e.send_method !== 'outlook' && e.send_method !== 'gmail');
    } else if (this.send_method_filter === 'personal') {
      filtered = filtered.filter(e => e.send_method === 'outlook' || e.send_method === 'gmail');
    }

    // Person filter
    if (this.emails_view === 'mine') {
      const me = this.globalService.GetCurrentUser();
      filtered = filtered.filter(e => e.creator_guid === me?.guid);
    } else if (this.emails_view === 'others' && this.selected_team_member) {
      filtered = filtered.filter(e => e.creator_guid === this.selected_team_member);
    }

    filtered = filtered.filter(e => this.MatchesQuickSearch(e));

    this.emails = filtered;
  }

  // Quick search (mirrors components/missing-info/table's quick search) - searches sent by, sent
  // to, system status, type, subject, and internal status, all resolved to their displayed plain
  // text rather than raw guids/numbers. Routed through ApplyViewFilter, the single place every
  // toolbar filter already runs through, so this applies on top of them automatically. Debounced
  // 300ms via quickSearchDebounceTimer so filtering doesn't recompute on every keystroke -
  // quickSearchInput tracks the raw typed value for instant display, quickSearchPending drives the
  // "Searching..." indicator while quickSearchTerm (the actually-applied value) is still catching up.
  quickSearchTerm = '';
  quickSearchInput = '';
  quickSearchPending = false;
  private quickSearchDebounceTimer: any;

  onQuickSearchChange(term: string) {
    this.quickSearchInput = term;
    this.quickSearchPending = true;
    clearTimeout(this.quickSearchDebounceTimer);
    this.quickSearchDebounceTimer = setTimeout(() => {
      this.quickSearchTerm = term;
      this.quickSearchPending = false;
      this.ApplyViewFilter();
    }, 300);
  }

  private MatchesQuickSearch(email: any): boolean {
    const query = this.quickSearchTerm?.trim().toLowerCase();
    if (!query) return true;
    const values = [
      email?.creator_display_name,
      email?.company_display_name || email?.linked_display_name,
      this.getEmailStatus(email?.status),
      this.emailTypeOptions.find(o => o.value === Number(email?.type))?.label,
      email?.subject,
      this.GetInternalStatusByGuid(email?.internal_status)?.title,
    ];
    return values.some(value => `${value ?? ''}`.toLowerCase().includes(query));
  }

  OnTeamMemberChange() {
    this.ApplyViewFilter();
  }

  async GetEmailInternalStatuses() {
    const data: any = await this.apiService.get('tags/emails_internal_status');
    if (data.success) this.emailInternalStatuses = data.data || [];
  }

  GetInternalStatusByGuid(guid: string) {
    if (!guid || !this.emailInternalStatuses?.length) return null;
    return this.emailInternalStatuses.find(s => s.guid === guid) || null;
  }

  trackStatus(_: number, item: any): string { return item.guid; }

  async EmailInternalStatusChange(email: any) {
    await this.apiService.put(`emails/${email.guid}`, { internal_status: email.internal_status });
  }

  async ToggleAutoRemind(email: any) {
    email.auto_remind = email.auto_remind ? 0 : 1;
    await this.apiService.put(`emails/${email.guid}`, { auto_remind: email.auto_remind });
  }

  IsAutoRemindExpired(email: any): boolean {
    return dayjs().isAfter(dayjs(email.created_date).add(14, 'day'));
  }

  GetAutoRemindTooltip(email: any): string {
    if (email.status == 3) return 'Email is completed — auto-remind is disabled';
    if (this.IsAutoRemindExpired(email)) return 'Reminder window has passed (sent more than 2 weeks ago)';
    if (!email.auto_remind) return 'Auto-remind off — click to enable';
    const stopDate = dayjs(email.created_date).add(14, 'day').format('MMM D, YYYY');
    if (email.use_firm_days_between) {
      return `Auto-remind on (firm default) · stops ${stopDate} · click to disable`;
    }
    const effectiveDays = email.days_between_email_reminders ?? 7;
    const baseDate = email.resent_date ? dayjs(email.resent_date) : dayjs(email.created_date);
    const nextSend = baseDate.add(effectiveDays, 'day').format('MMM D, YYYY');
    return `Auto-remind on (every ${effectiveDays} day${effectiveDays == 1 ? '' : 's'}) · next: ${nextSend} · stops ${stopDate} · click to disable`;
  }

  async OpenManageEmailInternalStatuses() {
    const modal = await this.modalController.create({
      component: TagsManageComponent,
      cssClass: 'tall-modal medium-modal',
      componentProps: {
        url_base: 'tags',
        url: 'emails_internal_status',
        title: 'Manage Email Internal Statuses',
        name: 'Internal Statuses',
      },
    });
    modal.onDidDismiss().then(async () => {
      await this.GetEmailInternalStatuses();
    });
    return await modal.present();
  }

  async OpenCreateModal(draft: any = null) {
    const modal = await this.modalController.create({
      component: CreatePage,
      cssClass: 'large-modal tall-modal',
      componentProps: draft ? { draft } : {}
    });
    modal.onDidDismiss().then(() => {
      this.GetData();
    });
    return await modal.present();
  }

  async OpenDraftsModal() {
    const modal = await this.modalController.create({
      component: DraftsPage,
      cssClass: 'medium-modal tall-modal secondary-modal'
    });
    modal.onDidDismiss().then((data: any) => {
      // The drafts list dismisses with the full loaded draft (guid/subject/data) once the user
      // picks one - hand it straight to the compose modal to auto-fill from.
      if (data && data.data) {
        this.OpenCreateModal(data.data);
      }
    });
    return await modal.present();
  }

  GetCommentTooltip(item: any): string {
    if (!item.email_last_comment_date) return 'No comments yet — click to add';
    return `Last comment: ${dayjs(this.globalService.DateToUTCFormat(item.email_last_comment_date)).format('MMM D, YYYY h:mm A')}`;
  }

  async OpenAddComment(item: any) {
    const modal = await this.modalController.create({
      component: QuickCommentComponent,
      cssClass: 'small-modal',
      componentProps: { url: 'emails/comments', guid: item.guid, title: item.subject, visible_to_client: true },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.success) item.email_comment_count = (item.email_comment_count ?? 0) + 1;
  }

  async OpenQuickComment(item: any) {
    const modal = await this.modalController.create({
      component: DetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: item.guid, email: item, activeIndex: 'comments' },
    });
    await modal.present();
  }

  async presentDetailsModal(email) {
    const modal = await this.modalController.create({
      component: DetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: email.guid }
    });
    modal.onDidDismiss().then((result: any) => {
      const d = result?.data;
      if (!d?.changed || !d?.email) return;
      const updated = d.email;
      const patch = (arr: any[]) => {
        const row = arr.find(e => e.guid === updated.guid);
        if (!row) return;
        row.auto_remind = updated.auto_remind;
        row.use_firm_days_between = updated.use_firm_days_between;
        row.days_between_email_reminders = updated.days_between_email_reminders;
        row.internal_status = updated.internal_status;
        row.status = updated.status;
        row.resent_date = updated.resent_date;
      };
      patch(this.all_emails);
      patch(this.emails);
    });
    return await modal.present();
  }

  // "Sent By" is always a team member (creator_guid) - mirrors home-detail-modal.component.ts's
  // ViewTeamMember, which opens the same DetailsComponent with just { guid }.
  async OpenSentByDetails(email: any) {
    if (!email?.creator_guid) return;
    const modal = await this.modalController.create({
      component: TeamMemberDetailsPage,
      cssClass: 'large-modal',
      componentProps: { team_member: { guid: email.creator_guid } },
      backdropDismiss: false
    });
    await modal.present();
  }

  // "Sent To" is a company (company_guid) when the email is routed to one, otherwise the linked
  // client/contact (linked_guid) directly - mirrors components/missing-info/table's
  // OpenClientEntityDetails, which picks between the same two details pages the same way.
  async OpenSentToDetails(email: any) {
    if (email?.company_guid) {
      const modal = await this.modalController.create({
        component: CompanyDetailsPage,
        cssClass: 'large-modal',
        componentProps: { company: { guid: email.company_guid } },
        backdropDismiss: false
      });
      await modal.present();
    } else if (email?.linked_guid) {
      const modal = await this.modalController.create({
        component: ClientDetailsPage,
        cssClass: 'large-modal',
        componentProps: { client: { guid: email.linked_guid } },
        backdropDismiss: false
      });
      await modal.present();
    }
  }

  async presentDeleteModal(email) {
    const modal = await this.modalController.create({
      component: DeletePage,
      cssClass: 'tall-modal medium-modal',
      componentProps: { email: email }
    });
    modal.onDidDismiss().then(async (data) => {
      // data is always set - data.data is only set when Delete was pressed (Cancel / X dismiss with null)
      if (data?.data) {
        await this.apiService.delete(`emails/${email.guid}`, data.data);
        this.GetData();
      }
    });
    return await modal.present();
  }

  async OpenEmailSignatureModal() {
    const modal = await this.modalController.create({
      component: SignaturePage,
      cssClass: 'large-modal'
    });
    return await modal.present();
  }

  async OpenTemplatesModal() {
    const modal = await this.modalController.create({
      component: TemplatesPage,
      cssClass: 'large-modal'
    });
    return await modal.present();
  }

  get canApplyDates(): boolean {
    if (!this.pendingStartDate || !this.pendingEndDate) return false;
    if (!this.current_start_date || !this.current_end_date) return true;
    return (
      dayjs(this.pendingStartDate).format('YYYY-MM-DD') !== dayjs(this.current_start_date).format('YYYY-MM-DD') ||
      dayjs(this.pendingEndDate).format('YYYY-MM-DD') !== dayjs(this.current_end_date).format('YYYY-MM-DD')
    );
  }

  applyDateFilter() {
    if (!this.canApplyDates) return;
    const start = dayjs(this.pendingStartDate).startOf('day').toDate();
    const end = dayjs(this.pendingEndDate).endOf('day').toDate();
    this.DateChanged([start, end]);
  }

  getTableDescription(): string {
    let ownerPart = '';
    if (this.emails_view === 'mine') {
      ownerPart = 'my ';
    } else if (this.emails_view === 'others' && this.selected_team_member) {
      const member = this.team_members.find(m => m.guid === this.selected_team_member);
      if (member) ownerPart = `${member.display_name || member.name}'s `;
    }

    let sourceLabel = '';
    if (this.emails_source_filter === 'manual') {
      sourceLabel = 'manually sent ';
    } else if (this.emails_source_filter === 'project') {
      sourceLabel = 'project ';
    } else if (this.emails_source_filter === 'missing_info') {
      sourceLabel = 'missing information ';
    }

    let methodLabel = '';
    if (this.send_method_filter === 'fusion') {
      methodLabel = ' sent through Fusion Workflow';
    } else if (this.send_method_filter === 'personal') {
      methodLabel = ' sent through your email service (Gmail / Outlook)';
    }

    let datePart = '';
    if (this.current_start_date && this.current_end_date) {
      const start = dayjs(this.current_start_date).format('MMM D, YYYY');
      const end = dayjs(this.current_end_date).format('MMM D, YYYY');
      datePart = ` from ${start} to ${end}`;
    }

    let quickSearchPart = '';
    if (this.quickSearchTerm?.trim()) {
      quickSearchPart = `, quick searching for: ${this.quickSearchTerm.trim()}`;
    }

    return `Showing ${ownerPart}${sourceLabel}emails${methodLabel}${datePart}${quickSearchPart}.`;
  }

  FormatDates() {
    this.current_start_date = dayjs().add(-3, 'month').startOf('day').toDate();
    this.current_end_date = dayjs().endOf('day').toDate();
    this.pendingStartDate = dayjs(this.current_start_date).format('YYYY-MM-DD');
    this.pendingEndDate = dayjs(this.current_end_date).format('YYYY-MM-DD');
  }

  DateChanged(value) {
    if (this.current_start_date != value[0] && this.current_end_date != value[1]) {
      this.current_start_date = value[0];
      this.current_end_date = value[1];
      this.pendingStartDate = dayjs(this.current_start_date).format('YYYY-MM-DD');
      this.pendingEndDate = dayjs(this.current_end_date).format('YYYY-MM-DD');
      this.GetData();
    }
  }

  async ResendEmail(email) {
    const alert = await this.alertController.create({
      header: 'Resend Email',
      message: 'Are you sure you want to resend this email?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Resend',
          handler: async () => {
            const data: any = await this.apiService.put(`emails/resend/${email.guid}`, null);
            this.globalService.presentToastMessage(data.message, data.success);
          }
        }
      ]
    });
    await alert.present();
  }

  getEmailStatus(status: number): string {
    switch (status) {
      case 0: return 'Unopened';
      case 1: return 'Viewed';
      case 2: return 'Files Downloaded';
      case 3: return 'Completed';
      default: return '';
    }
  }

  getEmailStatusClass(status: number): string {
    switch (status) {
      case 0: return 'badge bg-secondary';
      case 1: return 'badge bg-primary';
      case 2: return 'badge bg-warning text-dark';
      case 3: return 'badge bg-success';
      default: return 'badge bg-secondary';
    }
  }

}
